import {
  IDocumentAssemblerTemplate,
  IDocumentAssemblerTemplateVersionSummary,
} from '@commenda/api';
import type { ColumnGroup } from '@commenda/kakashi';
import { Badge, DataTable } from '@commenda/kakashi';
import { pluralize } from '@commenda/utils';
import moment from 'moment';

import { DocumentAssemblerVersionStatusBadge } from './DocumentAssemblerVersionStatusBadge';

interface DocumentAssemblerVersionsTableProps {
  template: IDocumentAssemblerTemplate;
  versions: IDocumentAssemblerTemplateVersionSummary[];
  isLoading: boolean;
  hasError: boolean;
  onRetry: () => void;
  onVersionOpen: (versionId: string) => void;
  onFirstVersionUpload: () => void;
}

export const DocumentAssemblerVersionsTable = ({
  template,
  versions,
  isLoading,
  hasError,
  onRetry,
  onVersionOpen,
  onFirstVersionUpload,
}: DocumentAssemblerVersionsTableProps) => {
  const columnGroups: ColumnGroup<
    IDocumentAssemblerTemplateVersionSummary,
    unknown
  >[] = [
    {
      id: 'versions',
      columns: [
        {
          id: 'version',
          accessorFn: (row) => `v${row.version}`,
          title: 'Version',
          header: 'Version',
          width: 200,
          cell: ({ row }) => (
            <div className="flex items-center gap-sm">
              <span className="txt-small-bold text-text-primary">
                v{row.original.version}
              </span>
              {row.original.isActiveVersion ? (
                <Badge variant="success" size="sm" hasDot>
                  Current active
                </Badge>
              ) : null}
            </div>
          ),
        },
        {
          id: 'status',
          accessorFn: (row) => row.status,
          title: 'Status',
          header: 'Status',
          width: 140,
          cell: ({ row }) => (
            <DocumentAssemblerVersionStatusBadge status={row.original.status} />
          ),
        },
        {
          id: 'problems',
          accessorFn: (row) => row.errorCount,
          title: 'Problems',
          header: 'Problems',
          width: 160,
          cell: ({ row }) => {
            if (row.original.hasPassedValidation) {
              return (
                <Badge variant="success" size="sm">
                  No problems
                </Badge>
              );
            }

            // A version can be unusable with nothing to list, when its stored check result cannot
            // be read. "0 problems" would be a lie, so it gets its own label.
            return (
              <Badge variant="error" size="sm">
                {row.original.errorCount > 0
                  ? pluralize(row.original.errorCount, 'problem')
                  : 'Cannot be used'}
              </Badge>
            );
          },
        },
        {
          id: 'conventions',
          accessorFn: (row) => (row.authorGuidance ? 'Set' : ''),
          title: 'Writing conventions',
          header: 'Writing conventions',
          width: 170,
          cell: ({ row }) => (
            <span className="txt-small-regular text-text-secondary">
              {row.original.authorGuidance ? 'Set' : '—'}
            </span>
          ),
        },
        {
          id: 'created',
          accessorFn: (row) => row.createdAt,
          title: 'Uploaded',
          header: 'Uploaded',
          width: 140,
          cell: ({ row }) => (
            <span className="txt-small-regular whitespace-nowrap text-text-secondary">
              {moment(row.original.createdAt).format('ll')}
            </span>
          ),
        },
      ],
    },
  ];

  return (
    <DataTable
      columnGroups={columnGroups}
      data={versions}
      isLoading={isLoading}
      onRowClick={(row) => onVersionOpen(row.id)}
      rowClassName={() => 'h-16 cursor-pointer'}
      errorConfig={
        hasError
          ? {
              type: 'server_error' as const,
              title: 'The versions could not be loaded',
              onRetry,
            }
          : undefined
      }
      emptyStateConfig={{
        title: 'No versions yet',
        description: template.archivedAt
          ? 'This template is archived, so no version can be added to it.'
          : 'Upload the Word file this template produces documents from.',
        ...(template.archivedAt
          ? {}
          : {
              action: {
                text: 'Upload the first version',
                onClick: onFirstVersionUpload,
              },
            }),
      }}
    />
  );
};
